import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import get from "lodash/get";
import { buildUrl, getEnv } from "shared/utils";
import Icon from "backpack-ui/dist/components/icon";
import Link from "shared/components/Link";
import { postClickthrough } from "../service";
import MaterialIcons from "./icons";
import styles from "./styles.css";

const BASE_URL = "https://www.lonelyplanet.com";
const SHOP_BASE_URL = "https://shop.lonelyplanet.com";

export const getResultIcon = (type, subtype) => {
  let ResultIcon = Icon.Map;
  const icons = {
    article: MaterialIcons.Description,
    lodging: MaterialIcons.LocalHotel,
    narratives: MaterialIcons.Description,
    news: MaterialIcons.Description,
    partner_activity: MaterialIcons.LocalActivity,
    place: Icon.Pin,
    product: MaterialIcons.Book,
    "thorntree-topic": MaterialIcons.Forum,
    video: MaterialIcons.LocalMovies,
  };

  if (icons[type]) {
    ResultIcon = icons[type];
  } else if (type === "poi") {
    const poiIcons = {
      Activities: MaterialIcons.LocalActivity,
      Attractions: MaterialIcons.PhotoCamera,
      Entertainment: MaterialIcons.LocalActivity,
      Events: Icon.Calendar,
      Hotels: MaterialIcons.LocalHotel,
      Information: Icon.Info,
      Nightlife: MaterialIcons.LocalBar,
      Restaurants: MaterialIcons.Restaurant,
      Shopping: MaterialIcons.LocalMall,
      Transportation: MaterialIcons.DirectionsTransit,
    };
    if (poiIcons[subtype]) {
      ResultIcon = poiIcons[subtype];
    }
  }

  return <ResultIcon className={styles.resultIcon} />;
};

const formatResultText = (result) => {
  const title = get(result, "highlights.title") || result.title;
  const parentName = get(result, "highlights.parent-name") || result.parentName;
  const text = parentName ? `${title}, ${parentName}` : title;

  return text;
};

const hasQuickLinks = (result) =>
  result.opType === "place" &&
  (result.placeType === "city" || result.placeType === "neighborhood");

const getResultQuickLinks = (result) => [
  {
    name: "Restaurants",
    url: buildUrl(BASE_URL, `a/poi-eat/${result.opId}`),
  },
  {
    name: "Hotels",
    url: buildUrl(BASE_URL, `a/lod/${result.opId}`),
  },
  {
    name: "Top things to do",
    url: buildUrl(BASE_URL, `a/poi/${result.opId}`),
  },
  {
    name: "Guidebooks",
    url: buildUrl(SHOP_BASE_URL, {
      path: "search",
      query: { q: result.title },
    }),
  },
];

const Results = ({ data, searchTerm, className }) => (
  <ul className={cn(styles.results, className)}>
    {data.map((result) => (
      <li className={styles.resultItem} key={result.id}>
        <Link
          href={result.url}
          className={styles.resultLink}
          onClick={() => postClickthrough(searchTerm, result.swiftypeId)}
        >
          {getResultIcon(result.opType, result.opSubtype)}
          <span
            className={styles.resultTitle}
            dangerouslySetInnerHTML={{ __html: formatResultText(result) }}
          />
        </Link>
        {hasQuickLinks(result) && (
          <div className={cn(styles.quickLinksWrapper)}>
            <ul>
              {getResultQuickLinks(result).map((link) => (
                <li key={link.name}>
                  <Link
                    className={cn(styles.quickLink, styles.resultLink)}
                    href={link.url}
                    isExternal={link.url.startsWith(SHOP_BASE_URL)}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </li>
    ))}

    <li className={styles.resultItem}>
      <Link
        href={buildUrl(getEnv("SEARCH_URL"), { query: { q: searchTerm } })}
        className={cn(styles.resultLink, styles.resultLinkAll)}
      >
        {`Show all results for “${searchTerm}”`}
      </Link>
    </li>
  </ul>
);

Results.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      opId: PropTypes.string,
      opSubtype: PropTypes.string,
      opType: PropTypes.string,
      parentName: PropTypes.string,
      placeType: PropTypes.string,
      swiftypeId: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ).isRequired,
  searchTerm: PropTypes.string.isRequired,
  className: PropTypes.string,
};

Results.defaultProps = {
  className: "",
};

export default Results;
